import { GameWeek, Season } from '../constants/id-constants';
import { TeamAbrvToTeamID, TID_LIST } from '../constants/league-constants';
import { ITeamLineScore } from './game.types';

// Generic types for the app
export type Maybe<T> = T | undefined;
export type StringOrNumber = string | number;
/** GameID that also contains the final score of the game eg "20250904_DAL@PHI=20:24" */
export type GameWithScoreID = `${GameID}=${StringOrNumber}:${StringOrNumber}`;
/** GameID that also contains the score of the game broken down by quarter eg "20250904_DAL@PHI=7,13,0,0,20:7,14,3,0,24" */
export type GameWithScoreByQtrID = `${GameID}=${string}:${string}`;
/** {YYYY} a four digit year of the NFL season represented as a string,
 * eg if season is played in 2025-26 the `SeasonID` will be `2025` */
export type SeasonID = Season | string;
/** consists of {YYYY}-{WeekID} eg 2024-01, 2024-02, ..., 2025-18 */
export type SeasonWeekID = `${SeasonID}-${WeekID}`;
export type TeamID = string;
export const BYE = 'BYE';
export type BYE_WEEK = typeof BYE;
export type GameID = string | BYE_WEEK;
export type GameWeekKey = keyof typeof GameWeek;
export type WeekID = string | GameWeek | GameWeekKey;
/** Bye week identifier, consists of {YYYY}-{WW}-{TT}-{TeamAbrv} where
 * WW is the WeekID number padded with leading 0 if week is less than 10
 * TT is the TeamID padded with leading 0 if team is less than 10 */
export type ByeWeekID = `${SeasonID}-${WeekID}-${TeamID}-${string}`; // last string is teamAbrv

export type TeamAbrvType = keyof typeof TeamAbrvToTeamID;

export type TeamAbrvID = `${TeamID}-${TeamAbrvType}`;

export type UID = string;

export type HeadToHeadID = `${TeamAbrvID}_${TeamAbrvID}`; // Format: '01-ARI_16-KC' (sorted)

export interface DeconstructedHeadToHeadID {
  headToHeadID: HeadToHeadID;
  team1AbrvID: TeamAbrvID;
  team2AbrvID: TeamAbrvID;
}

export interface DeconstructedSeasonTeamAbrvID extends DeconstructedTeamAbrvID {
  seasonID: SeasonID;
  teamAbrvID: TeamAbrvID;
}

export interface DeconstructedSeasonWeekID {
  yearID: SeasonID;
  weekID: WeekID;
  zeroPaddedWeekID: string;
  numericWeekID: number;
}

export interface DeconstructedSeasonWeekTeamAbrvID extends DeconstructedSeasonWeekID, DeconstructedSeasonTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
  seasonWeekID: SeasonWeekID;
}

export interface DeconstructedGameWithScoreID {
  homeTeamAbrv: TeamAbrvType;
  awayTeamAbrv: TeamAbrvType;
  homeTeamID: TeamID;
  awayTeamID: TeamID;
  homeTeamAbrvID: TeamAbrvID;
  awayTeamAbrvID: TeamAbrvID;
  homeTeamScore: number;
  awayTeamScore: number;
  gameID: GameID;
}

export type DeconstructedGameWithScoreByQtrID = {
  homeTeamLineScore: ITeamLineScore;
  awayTeamLineScore: ITeamLineScore;
  seasonWeekID: SeasonWeekID;
} & DeconstructedGameWithScoreID;

export interface DeconstructedTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
}

export interface DeconstructedSeasonTeamAbrvID extends DeconstructedTeamAbrvID {
  seasonID: SeasonID;
  teamAbrvID: TeamAbrvID;
}

export interface DeconstructedSeasonWeekID {
  yearID: SeasonID;
  weekID: WeekID;
  zeroPaddedWeekID: string;
  numericWeekID: number;
}

export interface DeconstructedSeasonWeekTeamAbrvID extends DeconstructedSeasonWeekID, DeconstructedSeasonTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
  seasonWeekID: SeasonWeekID;
}

export interface DeconstructedGameWithScoreID {
  homeTeamAbrv: TeamAbrvType;
  awayTeamAbrv: TeamAbrvType;
  homeTeamID: TeamID;
  awayTeamID: TeamID;
  homeTeamAbrvID: TeamAbrvID;
  awayTeamAbrvID: TeamAbrvID;
  homeTeamScore: number;
  awayTeamScore: number;
  gameID: GameID;
}

export type SeasonWeekTeamAbrvID = `${SeasonID}-${WeekID}-${TeamID}-${TeamAbrvType}`;

// zero padded weekID and TeamID e.g. `2025-01-01-ARI` Season2025, Week1, TeamID=1, TeamName=ARI
export type SeasonWeekTeamID = `${SeasonID}-${WeekID}-${TeamID}-${TeamAbrvType}`;
// zero padded weekID and TeamID e.g. `2025-01-ARI` Season2025, TeamID=01, TeamName=ARI
export type SeasonTeamAbrvID = `${SeasonID}-${TeamID}-${TeamAbrvType}`;

export interface DeconstructedSeasonWeekTeamAbrvID extends DeconstructedSeasonWeekID, DeconstructedSeasonTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
  seasonWeekID: SeasonWeekID;
  seasonTeamAbrvID: SeasonTeamAbrvID;
}

export interface DeconstructedGameID {
  homeTeamAbrv: TeamAbrvType;
  awayTeamAbrv: TeamAbrvType;
  homeTeamID: TeamID;
  awayTeamID: TeamID;
  homeTeamAbrvID: TeamAbrvID;
  awayTeamAbrvID: TeamAbrvID;
  gameID: GameID;
  gameDateString: string; // formatted as YYYYMMDD;
}

export interface DeconstructedGameWithScoreID extends DeconstructedGameID {
  homeTeamScore: number;
  awayTeamScore: number;
}

export type DecomposedUID = {
  yearID: SeasonID;
  weekNum: number;
  teamNum: number;
  weekID: WeekID;
  teamID: TeamID;
  teamAbrv: string;
  gameID: GameID;
  seasonWeekID: SeasonWeekID;
};

export type DeconstructedByeWeekID = {
  seasonID: SeasonID;
  weekID: WeekID;
  seasonWeekID: SeasonWeekID;
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
  teamAbrvID: TeamAbrvID;
  seasonTeamAbrvID: SeasonTeamAbrvID;
};

export type TID = (typeof TID_LIST)[number];

export type DeconstructedTID = {
  team4CharID: string;
  teamAbrv: string;
  teamNumID: number;
};

export interface DeconstructedTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
}

export interface DeconstructedSeasonTeamAbrvID extends DeconstructedTeamAbrvID {
  seasonID: SeasonID;
  teamAbrvID: TeamAbrvID;
}

export interface DeconstructedSeasonWeekID {
  yearID: SeasonID;
  weekID: WeekID;
  zeroPaddedWeekID: string;
  numericWeekID: number;
}

// export interface SeasonWeekTeamAbrvID

export interface DeconstructedSeasonWeekTeamAbrvID extends DeconstructedSeasonWeekID, DeconstructedSeasonTeamAbrvID {
  teamID: TeamID;
  teamAbrv: TeamAbrvType;
  seasonWeekID: SeasonWeekID;
  seasonTeamAbrvID: SeasonTeamAbrvID;
}
